import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Avatar,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Tooltip,
} from '@mui/material'
import {
  Add as AddIcon,
  Group as GroupIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Share as ShareIcon,
  MoreVert as MoreIcon,
  People as PeopleIcon,
  Hive as HiveIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material'
import { useAppSelector, useAppDispatch } from '@hooks/redux'
import {
  fetchApiariesAndGroups,
  deleteGroup,
} from '@store/slices/locationsSlice'
import { Group, GroupMember } from '@types'

/**
 * Groups Page Component
 * 
 * Displays and manages collaboration groups for sharing hives and data.
 * Features:
 * - View all user groups (owned and shared)
 * - Create new collaboration groups
 * - Edit group settings and members
 * - Share groups with other beekeepers
 * - View group statistics
 * 
 * @component
 */
const Groups: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const { groups, loadingState, error } = useAppSelector((state) => state.locations)
  const { user } = useAppSelector((state) => state.auth)

  const [menuAnchor, setMenuAnchor] = useState<{ [key: number]: HTMLElement | null }>({})

  useEffect(() => {
    dispatch(fetchApiariesAndGroups())
  }, [dispatch])

  const handleMenuOpen = (groupId: number, event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor({ ...menuAnchor, [groupId]: event.currentTarget })
  }

  const handleMenuClose = (groupId: number) => {
    setMenuAnchor({ ...menuAnchor, [groupId]: null })
  }

  const handleEdit = (groupId: number) => {
    navigate(`/groups/${groupId}/edit`)
    handleMenuClose(groupId)
  }

  const handleDelete = async (group: Group) => {
    if (window.confirm(`Are you sure you want to delete group "${group.name}"? This action cannot be undone.`)) {
      await dispatch(deleteGroup(group.id))
    }
    handleMenuClose(group.id)
  }

  const handleShare = async (group: Group) => {
    const shareText = `Join the SmartNyuki group "${group.name}"`
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/groups/${group.id}/edit`)
      window.alert('Group link copied. Share it with another beekeeper so they can request access.')
    } catch {
      window.alert(shareText)
    }
    handleMenuClose(group.id)
  }

  const getGroupRole = (group: Group) => {
    if (group.owner || group.user_role === 'owner') return 'Owner'
    if (group.admin || group.user_role === 'admin') return 'Admin'
    return group.user_role ? group.user_role : 'Member'
  }

  const getGroupColor = (group: Group) => {
    return group.hex_color || theme.palette.primary.main
  }

  const formatMemberCount = (members?: GroupMember[]) => {
    const count = members?.length || 0
    return `${count} member${count !== 1 ? 's' : ''}`
  }

  if (loadingState === 'pending') {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={50} />
        </Box>
      </Container>
    )
  }

  return (
    <Container sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Collaboration Groups
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/groups/create')}
        >
          New Group
        </Button>
      </Box>

      <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
        Collaborate with other beekeepers by sharing hives, data, and insights through groups.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Groups Grid */}
      {groups.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <GroupIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No Groups Yet
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Create your first collaboration group to start sharing hives and data with other beekeepers.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/groups/create')}
            >
              Create First Group
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {groups.map((group) => (
            <Grid item xs={12} sm={6} lg={4} key={group.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: `2px solid ${getGroupColor(group)}20`,
                  '&:hover': {
                    boxShadow: theme.shadows[4],
                  }
                }}
              >
                <CardContent sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                    <Avatar
                      sx={{ 
                        bgcolor: getGroupColor(group),
                        mr: 2,
                        width: 48,
                        height: 48,
                      }}
                    >
                      <GroupIcon />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="h6" component="h3" noWrap>
                        {group.name}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Chip
                          size="small"
                          label={getGroupRole(group)}
                          color={group.owner ? 'primary' : group.admin ? 'secondary' : 'default'}
                          icon={group.owner ? <AdminIcon /> : undefined}
                        />
                        {group.public && (
                          <Chip size="small" label="Public" variant="outlined" />
                        )}
                      </Box>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(group.id, e)}
                    >
                      <MoreIcon />
                    </IconButton>
                  </Box>

                  {group.description && (
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      {group.description}
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <HiveIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="textSecondary">
                        {group.hives?.length || 0} hives
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PeopleIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="textSecondary">
                        {formatMemberCount(group.members)}
                      </Typography>
                    </Box>
                  </Box>

                  {group.members && group.members.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="textSecondary">
                        Members:
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {group.members.slice(0, 3).map((member) => (
                          <Tooltip key={member.id} title={member.name || member.user_name || member.email || 'Member'}>
                            <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                              {(member.name || member.user_name || '?').charAt(0).toUpperCase()}
                            </Avatar>
                          </Tooltip>
                        ))}
                        {group.members.length > 3 && (
                          <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                            +{group.members.length - 3}
                          </Avatar>
                        )}
                      </Box>
                    </Box>
                  )}
                </CardContent>

                <CardActions>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleEdit(group.id)}
                    disabled={!group.editable && !group.owner}
                  >
                    {group.owner ? 'Manage' : 'View'}
                  </Button>
                  {group.owner && (
                    <Button
                      size="small"
                      startIcon={<ShareIcon />}
                      onClick={() => handleShare(group)}
                    >
                      Share
                    </Button>
                  )}
                </CardActions>

                {/* Context Menu */}
                <Menu
                  anchorEl={menuAnchor[group.id]}
                  open={Boolean(menuAnchor[group.id])}
                  onClose={() => handleMenuClose(group.id)}
                >
                  <MenuItem onClick={() => handleEdit(group.id)}>
                    <ListItemIcon>
                      <EditIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Edit Group</ListItemText>
                  </MenuItem>
                  {group.owner && (
                    <MenuItem onClick={() => handleShare(group)}>
                      <ListItemIcon>
                        <ShareIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Share Group</ListItemText>
                    </MenuItem>
                  )}
                  {group.owner && (
                    <MenuItem onClick={() => handleDelete(group)}>
                      <ListItemIcon>
                        <DeleteIcon fontSize="small" color="error" />
                      </ListItemIcon>
                      <ListItemText>Delete Group</ListItemText>
                    </MenuItem>
                  )}
                </Menu>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Info Card */}
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant="overline" display="block" gutterBottom>
            About Groups
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Groups allow you to collaborate with other beekeepers by sharing hives, inspection data, 
            and insights. As a group owner, you can invite members, manage permissions, and control 
            what data is shared. Members can view shared hives and contribute to collaborative research.
          </Typography>
        </CardContent>
      </Card>
    </Container>
  )
}

export default Groups
